<template>
  <v-chip v-if="isRecording" class="app-recording-indicator" color="white" :style="indicatorStyle"
    @pointerdown.native="onPointerDown" @click="handleClick">
    <v-icon small :color="isPaused ? 'grey darken-1' : 'red'" class="mr-2">mdi-microphone</v-icon>
    <span class="font-weight-medium mr-2">{{ label }}</span>
    <span class="grey--text text--darken-1 mr-1">{{ formattedDuration }}</span>

    <v-btn icon x-small class="ml-1" @pointerdown.native.stop @click.stop="togglePause">
      <v-icon small>{{ isPaused ? 'mdi-play' : 'mdi-pause' }}</v-icon>
    </v-btn>
    <v-btn icon x-small @pointerdown.native.stop @click.stop="handleStop">
      <v-icon small color="red">mdi-stop</v-icon>
    </v-btn>
  </v-chip>
</template>

<script>
const STORAGE_KEY = 'recordingIndicatorPosition';
const DRAG_THRESHOLD = 4;

export default {
  data() {
    return {
      position: null, // { top, left } em px; null = usa a posição padrão via CSS
      dragging: false,
      dragMoved: false,
      dragStart: null,
    };
  },

  created() {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (saved && typeof saved.top === 'number' && typeof saved.left === 'number') {
        this.position = saved;
      }
    } catch {
      this.position = null;
    }
  },

  beforeDestroy() {
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
  },

  computed: {
    positionStyle() {
      if (!this.position) return {};
      return { top: `${this.position.top}px`, left: `${this.position.left}px`, right: 'auto' };
    },

    indicatorStyle() {
      // borda vermelha discreta que reage ao nível de áudio captado (0..1)
      const spread = 2 + this.audioLevel * 8;
      const opacity = (0.25 + this.audioLevel * 0.45).toFixed(2);
      return {
        ...this.positionStyle,
        boxShadow: `0 2px 8px rgba(0, 0, 0, .2), 0 0 0 ${spread.toFixed(1)}px rgba(244, 67, 54, ${opacity})`,
      };
    },

    recorderState() {
      return this.$store.state.recorder.recorderState;
    },

    recordingMode() {
      return this.$store.state.recorder.recordingMode;
    },

    duration() {
      return this.$store.state.recorder.duration;
    },

    audioLevel() {
      return this.$store.state.recorder.audioLevel;
    },

    isPaused() {
      return this.$store.state.recorder.isPaused;
    },

    recordingSession() {
      return this.$store.state.recorder.recordingSession;
    },

    isRecording() {
      return this.recorderState === 'recording';
    },

    label() {
      if (this.isPaused) return 'Pausado';
      return this.recordingMode === 'meeting' ? 'Gravando reunião...' : 'Gravando...';
    },

    formattedDuration() {
      const m = Math.floor(this.duration / 60).toString().padStart(2, '0');
      const s = (this.duration % 60).toString().padStart(2, '0');
      return `${m}:${s}`;
    },
  },

  methods: {
    handleClick() {
      if (this.dragMoved) {
        this.dragMoved = false;
        return;
      }

      if (this.recordingSession) {
        this.$store.dispatch('recorder/setPendingReopen', this.recordingSession.id);
      }
    },

    togglePause() {
      if (this.isPaused) {
        this.$store.dispatch('recorder/resumeRecording');
      } else {
        this.$store.dispatch('recorder/pauseRecording');
      }
    },

    handleStop() {
      const sessionId = this.recordingSession && this.recordingSession.id;
      this.$store.dispatch('recorder/stopRecording');
      // Abre o Registro da sessão já com a gravação parada, pronta para transcrever/regravar.
      if (sessionId) {
        this.$store.dispatch('recorder/setPendingReopen', sessionId);
      }
    },

    onPointerDown(event) {
      const rect = this.$el.getBoundingClientRect();
      this.dragging = true;
      this.dragMoved = false;
      this.dragStart = {
        pointerX: event.clientX,
        pointerY: event.clientY,
        top: rect.top,
        left: rect.left,
      };
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
    },

    onPointerMove(event) {
      if (!this.dragging) return;

      const dx = event.clientX - this.dragStart.pointerX;
      const dy = event.clientY - this.dragStart.pointerY;
      if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
        this.dragMoved = true;
      }

      const maxLeft = window.innerWidth - this.$el.offsetWidth;
      const maxTop = window.innerHeight - this.$el.offsetHeight;

      this.position = {
        top: Math.min(Math.max(this.dragStart.top + dy, 0), maxTop),
        left: Math.min(Math.max(this.dragStart.left + dx, 0), maxLeft),
      };
    },

    onPointerUp() {
      this.dragging = false;
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);

      if (this.dragMoved && this.position) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.position));
      }
    },
  },
};
</script>

<style scoped>
.app-recording-indicator {
  position: fixed;
  top: 72px;
  right: 16px;
  z-index: 200;
  cursor: grab;
  touch-action: none;
  border: 1px solid rgba(244, 67, 54, .35);
  transition: box-shadow 100ms linear;
}

.app-recording-indicator:active {
  cursor: grabbing;
}
</style>
